// Renders sim entities to Phaser. Ally balls use composed sprites
// (sprite-composer.ts) so genome variation produces real per-ball visuals;
// enemies and projectiles use Graphics primitives (high churn, no genome).
// Overlays (HP bar, halo, fertility ring) draw on top via Graphics.

import Phaser from 'phaser';
import type { WorldState } from '../sim/world-state';
import { C } from '../sim/components';
import type {
  Position,
  Radius,
  RenderInfo,
  FactionComp,
  Health,
  Projectile,
  Age,
  Velocity,
  Stats,
  EmergentComp,
  GenomeComp,
  EnemyKindComp,
  XpComp,
} from '../sim/components';
import type { ComponentStore } from '../sim/ecs';
import { haloColorFor, outlineWidthForArmor } from './visual-mapping';
import { SpriteComposer } from './sprite-composer';
import { isTouchDevice } from '../input/device';

interface AllySpriteEntry {
  sprite: Phaser.GameObjects.Image;
  lastSeenFrame: number;
}

export class RunRenderer {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly composer: SpriteComposer;
  private readonly allySprites = new Map<number, AllySpriteEntry>();
  private readonly levelTexts = new Map<number, Phaser.GameObjects.Text>();
  private frame = 0;

  constructor(private readonly scene: Phaser.Scene) {
    this.g = scene.add.graphics().setDepth(10);
    this.composer = new SpriteComposer(scene);
  }

  draw(ws: WorldState): void {
    this.frame++;
    const { world } = ws;
    const positions = world.store<Position>(C.position);
    const velocities = world.store<Velocity>(C.velocity);
    const radii = world.store<Radius>(C.radius);
    const renders = world.store<RenderInfo>(C.render);
    const factions = world.store<FactionComp>(C.faction);
    const healths = world.store<Health>(C.health);
    const ages = world.store<Age>(C.age);
    const stats = world.store<Stats>(C.stats);
    const emergents = world.store<EmergentComp>(C.emergent);
    const genomes = world.store<GenomeComp>(C.genome);
    const kinds = world.store<EnemyKindComp>(C.enemyKind);
    const projectiles = world.store<Projectile>(C.projectile);
    const xps = world.store<XpComp>(C.xp);

    this.g.clear();

    positions.forEach((pos, id) => {
      if (projectiles.has(id)) return;
      const r = radii.get(id);
      const rinfo = renders.get(id);
      if (!r || !rinfo) return;
      const screen = worldToScreen(this.scene, pos);
      const side = factions.get(id)?.side;

      if (side === 'ally') {
        this.drawAlly(id, screen, r.r, genomes, emergents, velocities, stats, ws.time);
        // Level number above sprite (skip level 0).
        const xpComp = xps.get(id);
        if (xpComp && xpComp.level > 0) {
          let txt = this.levelTexts.get(id);
          if (!txt) {
            txt = this.scene.add
              .text(screen.x, screen.y - r.r - 10, '', {
                fontSize: '10px',
                color: '#ffe066',
                stroke: '#000000',
                strokeThickness: 2,
              })
              .setDepth(15)
              .setOrigin(0.5, 1);
            this.levelTexts.set(id, txt);
          }
          txt.setText(`Lv${xpComp.level}`);
          txt.setPosition(screen.x, screen.y - r.r - 10);
        }
      } else {
        this.drawEnemy(id, screen, r.r, rinfo.color, kinds, ws.time);
      }

      // HP bar (any entity with damage taken).
      const h = healths.get(id);
      if (h && h.current < h.max) {
        const frac = h.current / h.max;
        this.g.fillStyle(0x000000, 0.6);
        this.g.fillRect(screen.x - r.r, screen.y - r.r - 6, r.r * 2, 3);
        this.g.fillStyle(0x49d65b, 1);
        this.g.fillRect(screen.x - r.r, screen.y - r.r - 6, r.r * 2 * frac, 3);
      }

      // Fertility ring (allies only).
      if (side === 'ally') {
        const age = ages.get(id);
        if (age) {
          const t = age.lifespan > 0 ? age.seconds / age.lifespan : 0;
          if (t >= 0.25 && t <= 0.75) {
            this.g.lineStyle(1, 0xff9ec4, 0.8);
            this.g.strokeCircle(screen.x, screen.y, r.r + 3);
          }
        }
      }
    });

    // Projectiles on top.
    projectiles.forEach((_p, id) => {
      const pos = positions.get(id);
      const rinfo = renders.get(id);
      if (!pos || !rinfo) return;
      const screen = worldToScreen(this.scene, pos);
      this.g.fillStyle(rinfo.color, 1);
      this.g.fillCircle(screen.x, screen.y, 2.5);
    });

    // Sweep ally sprites: any entry not touched this frame is from a dead
    // ally; destroy its sprite to free GPU memory.
    for (const [eid, entry] of this.allySprites) {
      if (entry.lastSeenFrame !== this.frame) {
        entry.sprite.destroy();
        this.allySprites.delete(eid);
        const txt = this.levelTexts.get(eid);
        if (txt) {
          txt.destroy();
          this.levelTexts.delete(eid);
        }
      }
    }
  }

  destroy(): void {
    for (const e of this.allySprites.values()) e.sprite.destroy();
    this.allySprites.clear();
    for (const txt of this.levelTexts.values()) txt.destroy();
    this.levelTexts.clear();
    this.composer.destroy();
    this.g.destroy();
  }

  private drawEnemy(
    id: number,
    screen: { x: number; y: number },
    r: number,
    color: number,
    kinds: ComponentStore<EnemyKindComp>,
    time: number,
  ): void {
    const kind = kinds.get(id)?.kind ?? 'seeker';

    // Each enemy type pulses at a slightly different rate/phase for life.
    const pulseOffset = id * 0.37;
    const pulsedR = r * (1 + 0.07 * Math.sin(time * 3.0 + pulseOffset));

    this.g.fillStyle(color, 1);
    this.g.fillCircle(screen.x, screen.y, pulsedR);
    this.g.lineStyle(1, 0x000000, 1);
    this.g.strokeCircle(screen.x, screen.y, pulsedR);

    // Per-type visual mark inside the body.
    const s = pulsedR; // shorthand for mark sizing
    switch (kind) {
      case 'seeker': {
        // Directional chevron
        this.g.lineStyle(1.5, 0xffffff, 0.85);
        this.g.beginPath();
        this.g.moveTo(screen.x - s * 0.35, screen.y - s * 0.4);
        this.g.lineTo(screen.x + s * 0.4, screen.y);
        this.g.lineTo(screen.x - s * 0.35, screen.y + s * 0.4);
        this.g.strokePath();
        break;
      }
      case 'shooter': {
        // Crosshair
        const cr = s * 0.45;
        this.g.lineStyle(1.5, 0xffffff, 0.8);
        this.g.beginPath();
        this.g.moveTo(screen.x - cr, screen.y);
        this.g.lineTo(screen.x + cr, screen.y);
        this.g.moveTo(screen.x, screen.y - cr);
        this.g.lineTo(screen.x, screen.y + cr);
        this.g.strokePath();
        this.g.lineStyle(1, 0xffffff, 0.5);
        this.g.strokeCircle(screen.x, screen.y, s * 0.3);
        break;
      }
      case 'bomber': {
        // Concentric danger rings
        this.g.lineStyle(1.5, 0xff4400, 0.9);
        this.g.strokeCircle(screen.x, screen.y, s * 0.55);
        this.g.lineStyle(1, 0xffaa00, 0.6);
        this.g.strokeCircle(screen.x, screen.y, s * 0.28);
        break;
      }
      case 'swarmer': {
        // Small triangle pointing up
        const tr = s * 0.42;
        this.g.fillStyle(0xffffff, 0.7);
        this.g.fillTriangle(
          screen.x,
          screen.y - tr,
          screen.x - tr * 0.8,
          screen.y + tr * 0.6,
          screen.x + tr * 0.8,
          screen.y + tr * 0.6,
        );
        break;
      }
      case 'elite': {
        // Double gold ring — inner steady, outer pulses faster
        const outerPulse = r * (1 + 0.12 * Math.sin(time * 5.0 + pulseOffset));
        this.g.lineStyle(2, 0xffec5c, 0.85);
        this.g.strokeCircle(screen.x, screen.y, outerPulse + 3);
        this.g.lineStyle(1.5, 0xffe090, 0.6);
        this.g.strokeCircle(screen.x, screen.y, s * 0.5);
        break;
      }
    }
  }

  private drawAlly(
    id: number,
    screen: { x: number; y: number },
    r: number,
    genomes: ComponentStore<GenomeComp>,
    emergents: ComponentStore<EmergentComp>,
    velocities: ComponentStore<Velocity>,
    stats: ComponentStore<Stats>,
    time: number,
  ): void {
    const g = genomes.get(id);
    const e = emergents.get(id);
    if (!g) return;

    // Halo (emergent) — drawn into Graphics overlay so it pulses without
    // needing to recompose the texture. Skipped on touch for perf.
    if (!isTouchDevice() && e && e.ids.length > 0) {
      const halo = haloColorFor(e.ids);
      if (halo !== null) {
        const haloAlpha = 0.4 + 0.2 * Math.sin(time * 2.5 + id * 0.6);
        this.g.lineStyle(2, halo, haloAlpha);
        this.g.strokeCircle(screen.x, screen.y, r + 5);
      }
    }

    // Acquire / reuse sprite.
    const texKey = this.composer.textureFor(g.genome, e?.ids ?? []);
    let entry = this.allySprites.get(id);
    if (entry === undefined) {
      const sprite = this.scene.add.image(screen.x, screen.y, texKey).setDepth(5);
      entry = { sprite, lastSeenFrame: this.frame };
      this.allySprites.set(id, entry);
    } else if (entry.sprite.texture.key !== texKey) {
      entry.sprite.setTexture(texKey);
    }
    const e2 = entry;
    e2.sprite.setPosition(screen.x, screen.y);

    // Sprite is 64x64; scale so its visible radius matches the sim radius.
    const baseScale = ((r + 2) * 2) / 64;

    // Gentle bob: slightly varying scale over time, desync per ball.
    const bob = 1 + 0.04 * Math.sin(time * 2.2 + id * 0.71);

    // Velocity-based squash/stretch. The sprite is already rotated to point
    // along velocity (Y axis = travel direction), so scaleY stretches along
    // movement and scaleX squashes perpendicular to it.
    const v = velocities.get(id);
    let stretchX = 1;
    let stretchY = 1;
    if (v) {
      const sp = Math.hypot(v.vx, v.vy);
      if (sp > 4) {
        e2.sprite.setRotation(Math.atan2(v.vy, v.vx) + Math.PI / 2);
        const t = Math.min(1, (sp - 4) / 60);
        const str = 0.14 * t;
        stretchX = 1 - str * 0.6;
        stretchY = 1 + str;
      }
    }

    e2.sprite.setScale(baseScale * bob * stretchX, baseScale * bob * stretchY);
    e2.lastSeenFrame = this.frame;

    // Armor outline drawn on top of sprite.
    const s = stats.get(id);
    const armor = s?.sheet.armor ?? 0;
    if (armor > 1) {
      this.g.lineStyle(outlineWidthForArmor(armor), 0x101418, 1);
      this.g.strokeCircle(screen.x, screen.y, r + 1);
    }
  }
}

function worldToScreen(scene: Phaser.Scene, pos: Position): { x: number; y: number } {
  const { width, height } = scene.scale.gameSize;
  return { x: width / 2 + pos.x, y: height / 2 + pos.y };
}

export function screenToWorld(
  scene: Phaser.Scene,
  sx: number,
  sy: number,
): { x: number; y: number } {
  const { width, height } = scene.scale.gameSize;
  return { x: sx - width / 2, y: sy - height / 2 };
}
